import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Card,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Typography,
  Grid,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DataObjectIcon from "@mui/icons-material/DataObject";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import theme from "../theme";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const EntityFilter = ({
  sortBy,
  setSortBy,
  setFilterText,
  meta,
  selectedIntegrations,
  setSelectedIntegrations,
  selectedCapabilities,
  setSelectedCapabilities,
  entities,
}) => {
  const [searchInput, setSearchInput] = useState("");

  const handleSearchInputChange = (event) => {
    console.log(event.target.value);
    setSearchInput(event.target.value);
    setFilterText(event.target.value);
  };

  const handleClear = () => {
    setSearchInput("");
    setFilterText("");
  };

  const handleIntegrationChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedIntegrations(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleCapabilityChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedCapabilities(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleRemoveIntegration = (integrationToRemove) => {
    setSelectedIntegrations(
      selectedIntegrations.filter(
        (integration) => integration !== integrationToRemove
      )
    );
  };

  const handleRemoveCapability = (capabilityToRemove) => {
    setSelectedCapabilities(
      selectedCapabilities.filter(
        (capability) => capability !== capabilityToRemove
      )
    );
  };

  // Get unique integrations from meta that have at least one entity
  const availableIntegrations = useMemo(() => {
    if (!meta || !meta.integrationNames || !entities) return [];

    // Create a set of all integrations that have at least one entity
    const integrationsWithEntities = new Set();
    entities.forEach((entity) => {
      if (entity.integration) {
        integrationsWithEntities.add(entity.integration);
      }
    });

    // Return only the integrations that have entities
    return Object.keys(meta.integrationNames).filter((integration) =>
      integrationsWithEntities.has(integration)
    );
  }, [meta, entities]);

  // Get unique capabilities from entities
  const availableCapabilities = useMemo(() => {
    if (!entities || !meta || !meta.capabilityNames) return [];

    // Create a set of all capabilities that have at least one entity
    const capabilitiesWithEntities = new Set();
    entities.forEach((entity) => {
      if (entity.capabilities && Array.isArray(entity.capabilities)) {
        entity.capabilities.forEach((capability) => {
          capabilitiesWithEntities.add(capability);
        });
      }
    });

    return Array.from(capabilitiesWithEntities).sort();
  }, [entities, meta]);

  // Get capability display name from meta
  const getCapabilityDisplayName = (capability) => {
    return meta?.capabilityNames?.[capability] || capability;
  };

  // Determine if a capability is a data type (state/event) or operation type (command)
  const getCapabilityType = (capability) => {
    // This is a simplified logic - you may need to adjust based on your actual data structure
    const lowerCapability = capability.toLowerCase();

    // Check for common data-related keywords
    if (
      lowerCapability.includes("state") ||
      lowerCapability.includes("event") ||
      lowerCapability.includes("data") ||
      lowerCapability.includes("property") ||
      lowerCapability.includes("attribute")
    ) {
      return "data";
    }

    // Check for common operation-related keywords
    if (
      lowerCapability.includes("command") ||
      lowerCapability.includes("action") ||
      lowerCapability.includes("operation") ||
      lowerCapability.includes("function") ||
      lowerCapability.includes("method")
    ) {
      return "operation";
    }

    // Default to data if we can't determine
    return "data";
  };

  return (
    <Card
      sx={{
        mb: 1.5,
        width: "100%",
        borderRadius: 1,
      }}
      elevation={0}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "8px 12px",
          width: "100%",
          gap: 1.5,
        }}
      >
        <Grid container spacing={1.5}>
          {/* Name filter */}
          <Grid item xs={12} sm={4}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 500,
                color: "text.secondary",
                mb: 0.5,
                display: "block",
              }}
            >
              Name
            </Typography>
            <TextField
              value={searchInput}
              onChange={handleSearchInputChange}
              placeholder="Filter by name"
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                sx: {
                  backgroundColor: "background.input",
                  "& .MuiOutlinedInput-input": {
                    padding: "6px 10px",
                  },
                },
                endAdornment: searchInput && (
                  <IconButton
                    size="small"
                    onClick={handleClear}
                    sx={{ marginRight: -1 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </Grid>

          {/* Provider filter */}
          <Grid item xs={12} sm={4}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 500,
                color: "text.secondary",
                mb: 0.5,
                display: "block",
              }}
            >
              Provider
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                multiple
                value={selectedIntegrations}
                onChange={handleIntegrationChange}
                input={<OutlinedInput />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={meta?.integrationNames?.[value] || value}
                        size="small"
                        sx={{ height: 24 }}
                      />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {availableIntegrations.map((integration) => (
                  <MenuItem key={integration} value={integration}>
                    <Checkbox
                      checked={selectedIntegrations.indexOf(integration) > -1}
                      sx={{ mr: 1, p: 0.5 }}
                    />
                    <ListItemText
                      primary={
                        meta?.integrationNames?.[integration] || integration
                      }
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Capability filter */}
          <Grid item xs={12} sm={4}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 500,
                color: "text.secondary",
                mb: 0.5,
                display: "block",
              }}
            >
              Capability
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                multiple
                value={selectedCapabilities}
                onChange={handleCapabilityChange}
                input={<OutlinedInput />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={getCapabilityDisplayName(value)}
                        size="small"
                        sx={{ height: 24 }}
                        icon={
                          getCapabilityType(value) === "data" ? (
                            <DataObjectIcon fontSize="small" />
                          ) : (
                            <PlayArrowIcon fontSize="small" />
                          )
                        }
                      />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {availableCapabilities.map((capability) => {
                  const capabilityType = getCapabilityType(capability);
                  return (
                    <MenuItem key={capability} value={capability}>
                      <Checkbox
                        checked={selectedCapabilities.indexOf(capability) > -1}
                        sx={{ mr: 1, p: 0.5 }}
                      />
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {capabilityType === "data" ? (
                          <DataObjectIcon
                            fontSize="small"
                            sx={{ mr: 1, color: "text.secondary" }}
                          />
                        ) : (
                          <PlayArrowIcon
                            fontSize="small"
                            sx={{ mr: 1, color: "text.secondary" }}
                          />
                        )}
                        <ListItemText
                          primary={getCapabilityDisplayName(capability)}
                          secondary={
                            capabilityType === "data"
                              ? "Data (State/Event)"
                              : "Operation (Command)"
                          }
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

export default EntityFilter;
